import { useDispatch } from 'react-redux';
import {
  setResourcePanelView,
  setSelectedRequest,
  markTransportSeen,
  useIsResourcePanelOpen,
  useResourcePanelView,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  DEFAULT_GLOBAL_MAP_ZOOM,
  panToGlobalMap,
} from '@rsos/utils/panToGlobalMap';
import {
  AIR_AMBULANCE_SOURCE_BUTTON,
  trackRequestAirAmbulanceClick,
} from '../../../../../gainsightTrackings/airAmbulanceTrackings';
import {
  RESOURCE_REQUEST_ADAPTERS,
  type ResourceRequestDetailView,
  type ResourceRequestPickupLocation,
} from '../adapters';
import type { ResourceOption } from '../types';
import BreadCrumbs from './BreadCrumbs';
import NewRequestView from './NewRequestView';
import RequestListView from './RequestListView';
import { PanelContainer } from './ResourceRequestPanel.styles';

/**
 * ResourceRequestPanel is a right-hand slide-in panel that routes between
 * shell views list_requests and new, and per-adapter detail views such as
 * air_ambulance. PanelContainer is rendered here once so that view switches
 * do not unmount or remount it, preserving the width transition.
 */

function ResourceRequestPanel() {
  const dispatch = useDispatch();
  const isOpen = useIsResourcePanelOpen();
  const view = useResourcePanelView();

  const handleNewRequest = () => {
    dispatch(setSelectedRequest(null));
    dispatch(setResourcePanelView('new'));
  };

  const handleResourceClick = (resourceId: ResourceRequestDetailView) => {
    if (resourceId === 'air_ambulance') {
      trackRequestAirAmbulanceClick({
        sourceButton: AIR_AMBULANCE_SOURCE_BUTTON.RESOURCE_PANEL_OPTION,
      });
    }

    dispatch(setResourcePanelView(resourceId));
  };

  const handleSelectRequest = (
    detailView: ResourceRequestDetailView,
    requestId: string,
    pickupLocation: ResourceRequestPickupLocation | null,
  ) => {
    if (pickupLocation) {
      panToGlobalMap(
        pickupLocation.latitude,
        pickupLocation.longitude,
        DEFAULT_GLOBAL_MAP_ZOOM,
      );
    }

    dispatch(markTransportSeen(requestId));
    dispatch(setSelectedRequest(requestId));
    dispatch(setResourcePanelView(detailView));
  };

  const detailAdapter = RESOURCE_REQUEST_ADAPTERS.find(a => a.id === view);
  const DetailViewComponent = detailAdapter?.ViewComponent;

  return (
    <PanelContainer isOpen={isOpen} data-name="resource-request-panel">
      {view !== 'list_requests' && <BreadCrumbs />}
      {DetailViewComponent ? <DetailViewComponent /> : null}
      {view === 'new' && (
        <NewRequestView
          resourceOptions={RESOURCE_REQUEST_ADAPTERS.map(
            (adapter): ResourceOption => ({
              id: adapter.id,
              title: adapter.title,
              source: adapter.source,
              description: adapter.description,
              Icon: adapter.Icon,
            }),
          )}
          onResourceClick={handleResourceClick}
        />
      )}

      {
        // NOTE: We do not mount RequestListView unless the panel is open and
        //   the list view is active, so polling to etl_integrations only runs then.
        isOpen && view === 'list_requests' && (
          <RequestListView
            onNewRequest={handleNewRequest}
            onSelectRequest={handleSelectRequest}
          />
        )
      }
    </PanelContainer>
  );
}

export default ResourceRequestPanel;
